import { InspectionArea } from './planningTypes'
import { isScopeCompatible } from './scopeMeasurement'
import {
  MethodAssignment,
  MethodPlanning,
  TechniqueCatalogue,
} from './techniqueTypes'

export function resolveMethodAssignment(
  method: MethodAssignment,
  catalogue: TechniqueCatalogue
) {
  const technique = catalogue.techniques.find(
    (item) => item.id === method.techniqueId
  )
  const { estimatedScope, ...details } = method
  return {
    ...details,
    ...(estimatedScope ? { estimatedScope: { ...estimatedScope } } : {}),
    method: technique?.label ?? `Unavailable technique (${method.techniqueId})`,
    ...(technique?.scopeType ? { scopeType: technique.scopeType } : {}),
  }
}

export type ResolvedMethodAssignment = ReturnType<
  typeof resolveMethodAssignment
>

export function selectMethodAssignments(
  previous: readonly MethodAssignment[],
  techniqueIds: readonly string[],
  catalogue: TechniqueCatalogue,
  newId: () => string
): MethodAssignment[] {
  return [...new Set(techniqueIds)].flatMap((id) => {
    const existing = previous.find((item) => item.techniqueId === id)
    if (existing) return [existing]
    const technique = catalogue.techniques.find((item) => item.id === id)
    return technique
      ? [
          {
            id: newId(),
            techniqueId: id,
            procedure: technique.procedure ?? '',
            instructions: technique.instructions ?? '',
          },
        ]
      : []
  })
}

export function areMethodAssignmentsComplete(
  area: InspectionArea,
  catalogue: TechniqueCatalogue,
  config: MethodPlanning
) {
  const methods = area.methodAssignments
  const allowed = catalogue.degradationTypes.find(
    (type) => type.id === area.degradationTypeId
  )?.techniqueIds
  if (
    !methods.length ||
    new Set(methods.map((item) => item.id)).size !== methods.length ||
    new Set(methods.map((item) => item.techniqueId)).size !== methods.length
  )
    return false
  return methods.every((method) => {
    const technique = catalogue.techniques.find(
      (item) => item.id === method.techniqueId
    )
    if (!technique || !allowed?.includes(technique.id)) return false
    if (!config.requiredFields.every((field) => method[field].trim()))
      return false
    if (!technique.scopeType) return method.estimatedScope === undefined
    if (
      method.estimatedScope &&
      !isScopeCompatible(method.estimatedScope, technique.scopeType)
    )
      return false
    return (
      !config.requireEstimate ||
      (method.estimatedScope !== undefined && method.estimatedScope.num > 0)
    )
  })
}
